import { Aggregator, Sender } from "@energy-advice/alertfan-api-client";
import { useUpdateAggregators } from "api/api-hooks/alert-fan-hooks";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteParams } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import React, { useState } from "react";
import { Chips } from "primereact/chips";
import { InputSwitch } from "primereact/inputswitch";
import IsoNumberEditor from "./IsoNumberEditor";

interface IAggregatorsAndSendersCRUDProps {
  aggregators: Aggregator[];
  senders?: Sender[];
  checked: boolean;
  setChecked: (checked: boolean) => void;
}

const defaultAggregator: Aggregator = {
    tags: [],
    title: "new aggregator",
    proceed: false,
    groupWait: "PT10S",
    description: "",
    groupInterval: "PT5S",
    repeatInterval: "PT10S",
  };
  

const AggregatorsAndSendersCRUD = (props: IAggregatorsAndSendersCRUDProps) => {
    const [currentAggregators, setCurrentAggregators] = useState<Aggregator[]>(props.aggregators);
    const [currentSenders, setCurrentSenders] = useState<Sender[]>(props.senders ?? []);
  const updateAggregators = useUpdateAggregators();

  const handleTagsBodyTemplate = (rowData: Aggregator) => {
    return (
      <div className="flex gap-1 flex-wrap">
        {rowData.tags?.map((e: string) => <Tag value={e} key={e} className="h-2rem text-base" />)}
      </div>
    );
  };

  const handleTextEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value}
        onChange={e => options.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  const handleIsoNumberEditor = (options: ColumnEditorOptions) => {
    return <IsoNumberEditor options={options} />;
  };

  const handleTagsEditor = (options: ColumnEditorOptions) => {
    return <Chips value={options.value} onChange={e => options.editorCallback?.(e.value)} />;
  };

  const handleBooleanEditor = (options: ColumnEditorOptions) => {
    return (
      <InputSwitch checked={options.value} onChange={e => options.editorCallback?.(e.value)} />
    );
  };

  const handleRowDeleteButton = (options: ColumnBodyOptions) => {
    const updatedData = [...currentAggregators];
    updatedData.splice(options.rowIndex, 1);
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-danger"
        onClick={() => {
          confirmOnDelete(updatedData);
        }}
      />
    );
  };

  const confirmOnDelete = (updatedData: Aggregator[]) => {
    confirmPopup({
      message: "Are you sure you want to delete this aggregator?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        setCurrentAggregators(updatedData);
        updateAggregators.mutate(updatedData);
      },
    });
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteParams) => {
    const updatedData = [...currentAggregators];
    updatedData[e.index] = e.newData;
    setCurrentAggregators(updatedData);

    updateAggregators.mutate(updatedData);
  };

  const handleAddAggregator = () => {
    setCurrentAggregators([...currentAggregators, defaultAggregator]);
    updateAggregators.mutate([...currentAggregators, defaultAggregator]);
  };

  const handleTableHeaderTemplate = () => {
    return <Button icon="pi pi-plus" onClick={handleAddAggregator} />;
  };

  return (
    <div>
      <ConfirmPopup />      
        <div>
          <h1>Alerts Configuration</h1>
          <TabView activeIndex={props.checked ? 1 : 0} onTabChange={e => props.setChecked(e.index === 1)}>
            <TabPanel header="Aggregators">
              <DataTable
                header={handleTableHeaderTemplate}
                value={currentAggregators ?? []}
                editMode="row"
                onRowEditComplete={handleRowEditComplete}
              >
                <Column
                  field="title"
                  style={{ width: "18%" }}
                  header="Title"
                  editor={handleTextEditor}
                  sortable
                />
                <Column
                  field="description"
                  style={{ width: "17%" }}
                  header="Description"
                  editor={handleTextEditor}
                  sortable
                />
                <Column
                  field="tags"
                  style={{ width: "20%" }}
                  header="Tags"
                  body={handleTagsBodyTemplate}
                  editor={handleTagsEditor}
                />
                <Column
                  field="proceed"
                  style={{ width: "5%" }}
                  header="Proceed"
                  editor={handleBooleanEditor}
                  sortable
                />
                <Column
                  field="groupInterval"
                  style={{ width: "15%" }}
                  header="Group Interval"
                  editor={handleIsoNumberEditor}
                />
                <Column
                  field="repeatInterval"
                  style={{ width: "15%" }}
                  header="Repeat Interval"
                  editor={handleIsoNumberEditor}
                />
                <Column rowEditor style={{ width: "5%" }} header="Edit" />
                <Column
                  body={(rowData, options) => handleRowDeleteButton(options)}
                  style={{ width: "5%" }}
                  editor={
                    <Button icon="pi pi-trash" className="p-button-text p-button-danger" disabled />
                  }
                />
              </DataTable>
            </TabPanel>
            <TabPanel header="Senders">
              <DataTable value={currentSenders ?? []}>
                <Column field="title" header="Title" />
                <Column field="tags" header="Tags" />
              </DataTable>
            </TabPanel>
          </TabView>
        </div>
    </div>
  );
};

export default AggregatorsAndSendersCRUD;
